import React, { useEffect, useState, useCallback } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, extractErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import {
  Upload, X, UserPlus, Trash2, Sparkles, Shield, ShieldCheck, Users as UsersIcon, Crown, UserCircle,
  Key, Copy, Check, Plus, Lock,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const isOwnerRole = (role) => role === "owner" || role === "admin"; // legacy admin counts as owner

export default function Settings() {
  const { company, user, updateCompany } = useAuth();
  const { theme, setTheme } = useTheme();
  const [form, setForm] = useState({ company_name: company?.name || "", theme: theme, custom_logo_base64: company?.custom_logo_base64 || null });
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);

  const isOwner = isOwnerRole(user?.role) || user?.is_master;
  const plan = company?.plan || "free_trial";
  const canApi = plan === "enterprise" || plan === "free_trial";

  const loadUsers = () => api.get("/auth/users").then(({ data }) => setUsers(data.users || [])).catch(() => {});

  useEffect(() => { loadUsers(); }, []);
  useEffect(() => {
    setForm({ company_name: company?.name || "", theme: theme, custom_logo_base64: company?.custom_logo_base64 || null });
  }, [company, theme]);

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024) { toast.error("Logo must be <500 KB"); return; }
    const r = new FileReader();
    r.onload = () => setForm((p) => ({ ...p, custom_logo_base64: r.result }));
    r.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", form);
      updateCompany?.(data.company);
      setTheme(form.theme);
      toast.success("Settings saved");
    } catch (e) { toast.error(extractErr(e)); }
    setSaving(false);
  };

  const removeUser = async (u) => {
    if (!isOwner) { toast.error("Only the workspace owner can remove users"); return; }
    if (!window.confirm(`Remove ${u.email}?`)) return;
    try { await api.delete(`/auth/users/${u.id}`); toast.success("User removed"); loadUsers(); }
    catch (e) { toast.error(extractErr(e)); }
  };

  return (
    <AppShell title="Settings" subtitle="Workspace configuration, branding, and users">
      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile" data-testid="settings-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="branding" data-testid="settings-tab-branding">Branding</TabsTrigger>
          <TabsTrigger value="users" data-testid="settings-tab-users">Users</TabsTrigger>
          <TabsTrigger value="api" data-testid="settings-tab-api">API Keys</TabsTrigger>
          <TabsTrigger value="backup" data-testid="settings-tab-backup">Backup</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="rounded-2xl border border-border bg-card p-5 max-w-xl">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs">
                <Sparkles className="w-3 h-3" /> {company?.plan?.replace("_", " ") || ""}
              </span>
              <span className="text-muted-foreground capitalize">{company?.subscription_status}</span>
              <span className="text-muted-foreground">·</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-muted text-foreground px-2 py-0.5 text-xs" data-testid="settings-role-badge">
                {isOwner ? <Crown className="w-3 h-3 text-amber-500" /> : <UserCircle className="w-3 h-3" />}
                {isOwner ? "Owner" : "Member"}
              </span>
            </div>
            <div className="mt-4 space-y-3">
              <div className="space-y-1.5">
                <Label>Company name</Label>
                <Input value={form.company_name} onChange={(e) => setForm((p) => ({ ...p, company_name: e.target.value }))} data-testid="settings-company-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Theme</Label>
                <div className="grid grid-cols-2 gap-2">
                  {["dark", "light"].map((t) => (
                    <button type="button" key={t} onClick={() => setForm((p) => ({ ...p, theme: t }))} className={`rounded-lg border px-3 py-2.5 text-sm capitalize transition-colors ${form.theme === t ? "border-primary ring-2 ring-primary/25 bg-primary/5" : "border-border hover:bg-accent/70"}`} data-testid={`settings-theme-${t}`}>
                      {t} mode
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={save} disabled={saving} data-testid="settings-save-button">{saving ? "Saving…" : "Save changes"}</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="branding">
          <div className="rounded-2xl border border-border bg-card p-5 max-w-xl">
            <h3 className="font-heading font-semibold">Custom company logo</h3>
            <p className="text-sm text-muted-foreground">Your logo replaces the Operon logo in the sidebar and PDF exports. (Appears in light mode only.)</p>
            <div className="mt-4 flex items-center gap-4">
              {form.custom_logo_base64 ? (
                <div className="relative">
                  <img src={form.custom_logo_base64} alt="logo" className="h-14 w-auto rounded-lg object-contain border border-border bg-white p-1" />
                  <button type="button" className="absolute -top-1 -right-1 rounded-full bg-background border border-border p-1" onClick={() => setForm((p) => ({ ...p, custom_logo_base64: null }))}><X className="w-3 h-3" /></button>
                </div>
              ) : (
                <div className="h-14 w-28 rounded-lg bg-muted flex items-center justify-center text-xs text-muted-foreground">No logo</div>
              )}
              <label className="flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm cursor-pointer hover:bg-accent/50">
                <Upload className="w-4 h-4" /> Upload PNG / SVG
                <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="settings-logo-input" />
              </label>
            </div>
            <Button className="mt-4" onClick={save} disabled={saving} data-testid="settings-branding-save">{saving ? "Saving…" : "Save branding"}</Button>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="rounded-2xl border border-border bg-card p-5 max-w-2xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading font-semibold flex items-center gap-2">
                  <UsersIcon className="w-4 h-4 text-muted-foreground" /> Users
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Anyone in your workspace can use every feature included in your <span className="capitalize">{company?.plan?.replace("_", " ") || "current"}</span> plan.
                  Only the <b>owner</b> can invite or remove users and change billing.
                </p>
              </div>
              {isOwner && (
                <Button size="sm" onClick={() => setAddOpen(true)} data-testid="settings-users-add-button">
                  <UserPlus className="w-4 h-4 mr-1.5" />Invite user
                </Button>
              )}
            </div>
            {!isOwner && (
              <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-300 flex items-center gap-2" data-testid="settings-users-readonly-notice">
                <Shield className="w-3.5 h-3.5" />
                <span>You're viewing as a member. Ask the workspace owner to manage users.</span>
              </div>
            )}
            <div className="mt-3 divide-y divide-border">
              {users.map((u) => {
                const own = isOwnerRole(u.role);
                return (
                  <div key={u.id} className="py-2 flex items-center justify-between gap-2" data-testid={`settings-user-row-${u.id}`}>
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate flex items-center gap-1.5">
                        {own ? <Crown className="w-3.5 h-3.5 text-amber-500" /> : <UserCircle className="w-3.5 h-3.5 text-muted-foreground" />}
                        {u.full_name || u.email}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {u.email} · <span className="capitalize">{own ? "Owner" : "Member"}</span>
                      </div>
                    </div>
                    {isOwner && !own && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeUser(u)} data-testid={`settings-user-remove-${u.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <AddUserDialog open={addOpen} onClose={() => setAddOpen(false)} onCreated={loadUsers} />
        </TabsContent>

        <TabsContent value="api">
          <ApiKeysTab isOwner={isOwner} canApi={canApi} />
        </TabsContent>

        <TabsContent value="backup">
          <BackupTab isOwner={isOwner} />
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}

function BackupTab({ isOwner }) {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/backup/snapshots");
      setSnapshots(data.snapshots || []);
    } catch (e) { /* non-owner gets 403 — show locked state */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const downloadNow = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("operon_token");
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/backup/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("export failed");
      const blob = await resp.blob();
      const cd = resp.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fn = m ? m[1] : "operon-backup.zip";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fn; a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) { toast.error("Export failed"); }
    setBusy(false);
  };

  const downloadSnapshot = async (filename) => {
    try {
      const token = localStorage.getItem("operon_token");
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/backup/snapshots/${encodeURIComponent(filename)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("download failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const triggerSnapshot = async () => {
    setBusy(true);
    try {
      await api.post("/backup/snapshot");
      toast.success("Snapshot created");
      load();
    } catch (e) { toast.error(extractErr(e, "Snapshot failed")); }
    setBusy(false);
  };

  if (!isOwner) {
    return (
      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5 text-sm max-w-2xl">
        <Shield className="w-4 h-4 inline mr-1.5 text-amber-500" />
        Only the workspace owner can manage backups.
      </div>
    );
  }

  const fmtSize = (n) => n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(2)} MB`;

  return (
    <div className="rounded-2xl border border-border bg-card p-5 max-w-3xl">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-heading font-semibold">Data Backup & Protection</h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">
            Your data is automatically backed up <b>every 24 hours</b> and the most recent <b>14 snapshots</b> are retained — on every plan, free or paid. You can also download a fresh export of all your data at any time.
          </p>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <Button size="sm" onClick={downloadNow} disabled={busy} data-testid="backup-download-now"><Sparkles className="w-4 h-4 mr-1.5" />Download backup now</Button>
          <Button size="sm" variant="outline" onClick={triggerSnapshot} disabled={busy} data-testid="backup-snapshot-now">Take snapshot</Button>
        </div>
      </div>
      <div className="mt-4 rounded-lg border border-border">
        <div className="px-3 py-2 border-b border-border text-xs font-medium uppercase tracking-wide text-muted-foreground">Daily snapshots</div>
        {loading ? (
          <div className="p-4 text-sm text-muted-foreground">Loading…</div>
        ) : snapshots.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            No snapshots yet — the first one is created within 24 hours of registration. Click <b>Take snapshot</b> above to create one now.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {snapshots.map((s) => (
              <div key={s.filename} className="flex items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`backup-row-${s.filename}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs truncate">{s.filename}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {new Date(s.created_at).toLocaleString()} · {fmtSize(s.size_bytes)}
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => downloadSnapshot(s.filename)} data-testid={`backup-download-${s.filename}`}>Download</Button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/[0.04] p-3 text-xs text-emerald-700 dark:text-emerald-400 flex items-start gap-2">
        <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          All data is stored in MongoDB with TLS in transit. Backups are written to the server's local filesystem with restricted access. We recommend downloading periodic copies to your own cloud storage for off-site redundancy.
        </div>
      </div>
    </div>
  );
}

function ApiKeysTab({ isOwner, canApi }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState({ read: true, write: true });
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!canApi) { setLoading(false); return; }
    setLoading(true);
    try { const { data } = await api.get("/api-keys"); setKeys(data.keys || []); }
    catch (e) { /* enterprise gate may 403 - silently ignore */ }
    setLoading(false);
  }, [canApi]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Key name is required"); return; }
    const scopesArr = Object.entries(scopes).filter(([, v]) => v).map(([k]) => k);
    if (!scopesArr.length) { toast.error("Select at least one scope"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/api-keys", { name: name.trim(), scopes: scopesArr });
      setNewKey(data);
      setName(""); setScopes({ read: true, write: true });
      load();
    } catch (e) { toast.error(extractErr(e)); }
    setCreating(false);
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revoke key "${k.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/api-keys/${k.id}`); toast.success("Key revoked"); load(); }
    catch (e) { toast.error(extractErr(e)); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(newKey.key);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch (e) { toast.error("Copy failed — please copy manually"); }
  };

  if (!canApi) {
    return (
      <div className="rounded-2xl border border-border bg-card p-5 max-w-2xl">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Key className="w-5 h-5" /></div>
          <div>
            <h3 className="font-heading font-semibold">API Access</h3>
            <p className="text-sm text-muted-foreground mt-1">API keys let you integrate Operon with external systems (ERP, marketplaces, custom dashboards). Available on the <b>Enterprise</b> plan.</p>
            <Button className="mt-3" asChild><a href="/app/billing"><Sparkles className="w-4 h-4 mr-1.5" />Upgrade to Enterprise</a></Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-5 max-w-3xl">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-heading font-semibold flex items-center gap-2"><Key className="w-4 h-4 text-muted-foreground" /> API Keys</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Use keys in the <code className="font-mono text-[11px] px-1 py-0.5 rounded bg-muted">X-API-Key</code> header to call <code className="font-mono text-[11px] px-1 py-0.5 rounded bg-muted">/api/v1/*</code> endpoints. Keys are shown once at creation. Only the <b>owner</b> can manage keys.
          </p>
        </div>
        {isOwner && (
          <Button size="sm" onClick={() => { setOpenCreate(true); setNewKey(null); }} data-testid="settings-api-create-button">
            <Plus className="w-4 h-4 mr-1.5" />New key
          </Button>
        )}
      </div>
      {!isOwner && (
        <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-300 flex items-center gap-2">
          <Shield className="w-3.5 h-3.5" />
          <span>Only the workspace owner can create or revoke API keys.</span>
        </div>
      )}

      <div className="mt-3 divide-y divide-border">
        {loading ? (
          <div className="text-sm text-muted-foreground py-4">Loading…</div>
        ) : keys.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground text-sm">
            <Lock className="w-6 h-6 mx-auto mb-2 opacity-50" />
            No API keys yet. Create one to start integrating Operon with your tools.
          </div>
        ) : (
          keys.map((k) => (
            <div key={k.id} className="py-3 flex items-center justify-between gap-3" data-testid={`api-key-row-${k.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm truncate flex items-center gap-2">
                  {k.name}
                  {k.revoked && <span className="rounded-full bg-destructive/10 text-destructive text-[10px] uppercase tracking-wide px-1.5 py-0.5">Revoked</span>}
                  <span className="rounded-full bg-muted text-muted-foreground text-[10px] uppercase tracking-wide px-1.5 py-0.5">{(k.scopes || []).join(" · ")}</span>
                </div>
                <div className="flex flex-wrap gap-x-3 mt-0.5 text-xs text-muted-foreground">
                  <span className="font-mono">{k.key_prefix}…</span>
                  {k.created_at && <span>Created {new Date(k.created_at).toLocaleDateString()}</span>}
                  {k.last_used_at ? <span>Last used {new Date(k.last_used_at).toLocaleString()}</span> : <span>Never used</span>}
                </div>
              </div>
              {isOwner && !k.revoked && (
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => revoke(k)} data-testid={`api-key-revoke-${k.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))
        )}
      </div>

      <Dialog open={openCreate} onOpenChange={(v) => !v && (setOpenCreate(false), setNewKey(null))}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{newKey ? "Your new API key" : "Create API key"}</DialogTitle>
            <DialogDescription>
              {newKey ? "This is the only time the secret will be shown. Copy and store it securely — you won't be able to see it again." : "Name your key so you can identify it later. Scopes control what the key can do."}
            </DialogDescription>
          </DialogHeader>
          {newKey ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
                <div className="text-[11px] text-emerald-700 dark:text-emerald-400 uppercase tracking-wide mb-1">Secret key (copy now)</div>
                <div className="font-mono text-xs break-all" data-testid="api-key-created-secret">{newKey.key}</div>
              </div>
              <Button onClick={copy} variant="outline" className="w-full" data-testid="api-key-copy-button">
                {copied ? <><Check className="w-4 h-4 mr-1.5 text-emerald-500" />Copied</> : <><Copy className="w-4 h-4 mr-1.5" />Copy to clipboard</>}
              </Button>
              <DialogFooter>
                <Button onClick={() => { setOpenCreate(false); setNewKey(null); }}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={create} className="space-y-3">
              <div className="space-y-1.5">
                <Label>Key name *</Label>
                <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. ERP integration" data-testid="api-key-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Scopes</Label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 cursor-pointer hover:bg-accent/50">
                    <input type="checkbox" checked={scopes.read} onChange={(e) => setScopes((s) => ({ ...s, read: e.target.checked }))} data-testid="api-key-scope-read" />
                    <div className="text-sm"><div className="font-medium">Read</div><div className="text-[11px] text-muted-foreground">List & get endpoints</div></div>
                  </label>
                  <label className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 cursor-pointer hover:bg-accent/50">
                    <input type="checkbox" checked={scopes.write} onChange={(e) => setScopes((s) => ({ ...s, write: e.target.checked }))} data-testid="api-key-scope-write" />
                    <div className="text-sm"><div className="font-medium">Write</div><div className="text-[11px] text-muted-foreground">Create intakes & outtakes</div></div>
                  </label>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpenCreate(false)}>Cancel</Button>
                <Button type="submit" disabled={creating} data-testid="api-key-create-submit">{creating ? "Creating…" : "Create key"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AddUserDialog({ open, onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [sub, setSub] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSub(true);
    try {
      await api.post("/auth/invite", { email, full_name: fullName });
      toast.success(`Invitation sent to ${email}`);
      setSent(true);
      onCreated();
    } catch (e) { toast.error(extractErr(e)); }
    setSub(false);
  };
  const reset = () => { setEmail(""); setFullName(""); setSent(false); onClose(); };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && reset()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Invite user by email</DialogTitle>
          <DialogDescription>Invited users join as <b>members</b> with full access to your plan's features.</DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm">
            <div className="font-medium text-emerald-600 dark:text-emerald-400">Invitation sent</div>
            <p className="text-muted-foreground mt-1">{email} will receive a secure link to set their password and join your workspace. The link expires in 72 hours.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5"><Label>Full name (optional)</Label><Input value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="invite-name-input" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email-input" /></div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={reset}>Cancel</Button>
              <Button type="submit" disabled={sub} data-testid="invite-submit-button">{sub ? "Sending…" : "Send invitation"}</Button>
            </DialogFooter>
          </form>
        )}
        {sent && (
          <DialogFooter><Button onClick={reset}>Done</Button></DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
